// Copyright (c) Docugami, Inc. All rights reserved.

import { DirectionalHint, ISpinnerStyles, Spinner, TooltipHost } from "@fluentui/react"
import { dgFontFamily } from "components/common/Fonts";
import { emptyColor, primaryTheme, themeBlue, themeWhite } from "components/shell/presentation/AppStyles"
import { useMemo } from "react";
import styled from "styled-components"

export interface IBadgeProps {
    badgeColor?: string;
    badgeCount?: number;
    spinnerActive?: boolean;
    spinnerColor?: string;
    badgeSize?: IBadgeSize;
    badgeFontColor?: string;
    tooltip?: string;
}

export interface IBadgeSize {
    outerBorderWidth: number;
    innerBorderWidth: number;
    spinnerBorderWidth: number;
    spinnerSize: number;
    badgeSize: number;
}

const defaultBadgeSize: IBadgeSize = {
    outerBorderWidth: 1,
    innerBorderWidth: 3,
    spinnerBorderWidth: 2,
    spinnerSize: 20,
    badgeSize: 22
}

const BadgeFont = styled.span`
    font-family: ${dgFontFamily};
    font-size: 9px;
    line-height: 10px;
    font-weight: 600;
`;

const BadgeContainer = styled.div<{ size: number, innerBorderWidth: number, outerBorderWidth: number, bgColor: string, fontColor: string }>`
    border-radius: 50%;
    width: ${props => props.size}px;
    height: ${props => props.size}px;
    box-shadow: inset 0 0 0 ${props => props.outerBorderWidth}px ${emptyColor}, inset 0 0 0 ${props => props.innerBorderWidth}px ${themeWhite};
    background-color: ${props => props.bgColor};
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    color: ${props => props.fontColor};
`

export const Badge = (props: IBadgeProps): JSX.Element => {
    const badgeSize = props.badgeSize ?? defaultBadgeSize;
    const spinnerStyle: Partial<ISpinnerStyles> = {
        root: {
            position: "absolute",
        },
        circle: {
            width: `${badgeSize.spinnerSize}px`,
            height: `${badgeSize.spinnerSize}px`,
            borderWidth: `${badgeSize.spinnerBorderWidth}px`,
            borderColor: `${props.spinnerColor ?? primaryTheme.palette.themePrimary} ${themeWhite} ${themeWhite}`
        }
    }

    const [badgeString, isOverflow] = useMemo(() => {
        const maxPlaceHolder = '99';
        const value = getBadgeString(props.badgeCount, 2, maxPlaceHolder);
        return [value, value === maxPlaceHolder]
    }, [props.badgeCount]);

    return (
        <TooltipHost content={isOverflow ? `${props.badgeCount}` : props.tooltip ?? ''} directionalHint={DirectionalHint.rightCenter}>
            <BadgeContainer
                size={badgeSize.badgeSize}
                outerBorderWidth={badgeSize.outerBorderWidth}
                innerBorderWidth={badgeSize.innerBorderWidth}
                bgColor={props.badgeColor ?? themeBlue}
                fontColor={props.badgeFontColor ?? themeWhite}>
                { !!props.spinnerActive && <Spinner styles={spinnerStyle} /> }
                <BadgeFont>{badgeString}</BadgeFont>
            </BadgeContainer>
        </TooltipHost>
    )
}

function getBadgeString(badgeCount?: number, maxDigits: number = 2, maxPlaceholder: string = '99'): string {
    if (!badgeCount) {
        return '';
    }

    let numDigits = 0;
    let value = badgeCount;
    while (value > 0 && numDigits <= maxDigits) {
        numDigits++;
        value = Math.trunc(value / 10);
    }

    if (numDigits > maxDigits) {
        return maxPlaceholder;
    }

    return `${badgeCount}`;
}
